import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

export default function BusinessGallery({ images = [], name = "" }) {
  const [open, setOpen] = useState(null);
  const n = images.length;

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i - 1 + n) % n), [n]);
  const next = useCallback(() => setOpen((i) => (i + 1) % n), [n]);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, close, prev, next]);

  if (!n) return null;

  return (
    <section data-testid="biz-gallery">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <Camera size={22} className="text-[#E05638]" /> La vitrine en images
      </h2>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {images.map((img, i) => (
          <button
            key={`${i}-${img}`}
            onClick={() => setOpen(i)}
            data-testid={`biz-gallery-img-${i}`}
            className={`group relative overflow-hidden rounded-2xl bg-slate-100 border border-slate-200 ${i === 0 ? "col-span-2 row-span-2 aspect-square sm:aspect-auto" : "aspect-square"}`}
          >
            <img src={img} alt={`${name} ${i + 1}`} loading="lazy"
                 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
            <span className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/15 transition-colors" />
          </button>
        ))}
      </div>

      {open !== null && (
        <div className="fixed inset-0 z-[100] bg-slate-950/92 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={close} data-testid="biz-lightbox">
          <button onClick={close} data-testid="biz-lightbox-close"
                  className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
            <X size={22} />
          </button>
          {n > 1 && (
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="biz-lightbox-prev"
                    className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
              <ChevronLeft size={24} />
            </button>
          )}
          <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={images[open]} alt={`${name} ${open + 1}`} className="max-h-[80vh] w-auto rounded-xl object-contain shadow-2xl" />
            <figcaption className="mt-3 text-center text-white/80 text-sm font-semibold">
              {name} · Photo {open + 1} / {n}
            </figcaption>
          </figure>
          {n > 1 && (
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="biz-lightbox-next"
                    className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
